import { createContext, createElement, lazy, useContext, type ComponentType } from "react";
import { importFresh } from "./import-fresh";

// `never` accepts a component with any props without resorting to `any`.
type AnyComponent = ComponentType<never>;
type PropsOf<C> = C extends ComponentType<infer P> ? P : never;

/**
 * The navigation a lazy route renders under. `LazyRouteBoundary` provides the
 * location key it resets on, so a route whose import failed is recreated on the
 * next navigation instead of replaying the cached rejection. Outside a boundary
 * the value is null and a failed import stays failed, as with plain `lazy`.
 */
export const LazyNavigationContext = createContext<string | null>(null);

/** The failed module's URL, same origin only, with a cache-busting query. */
function freshModuleUrl(error: unknown): string | null {
  const message = (error as { message?: unknown } | null)?.message;
  if (typeof message !== "string") return null;
  const match = /(?:https?:\/\/|\/)[^\s'"]+?\.m?js(?:\?[^\s'"]*)?(?=$|[\s'"])/.exec(message);
  if (!match) return null;
  const page = new URL(window.location.href);
  const url = new URL(match[0], page);
  if (url.origin !== page.origin) return null;
  url.searchParams.set("retry", Date.now().toString(36));
  return url.href;
}

function isComponent(value: unknown): boolean {
  return typeof value === "function" || (typeof value === "object" && value !== null && "$$typeof" in value);
}

function isModuleObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
    && (value as { [Symbol.toStringTag]?: unknown })[Symbol.toStringTag] === "Module";
}

/**
 * The named export of a freshly imported chunk. A page that also lives in a
 * shared chunk is not exported by name: Rollup re-exports its namespace under
 * a short name (`export{Ea as i}`, loaded as `.then(e=>e.i)`), so the one
 * module object carrying the export is used. More than one is ambiguous.
 */
function exportFrom(namespace: Record<string, unknown>, exportName: string): unknown {
  if (isComponent(namespace[exportName])) return namespace[exportName];
  const carriers = Object.values(namespace).filter(
    (value): value is Record<string, unknown> => isModuleObject(value) && isComponent(value[exportName]),
  );
  return carriers.length === 1 ? carriers[0][exportName] : null;
}

/**
 * `React.lazy` for a route or panel module, with two recoveries and no reload:
 *
 * 1. A failed import is retried once. When the browser's error names the
 *    module, the retry imports that URL with a cache-busting query and takes
 *    `exportName` from it, directly or from the one module object that
 *    carries it. When the error names no URL (Safari), the fresh module does
 *    not carry the export, or the export is `default` (a chunk never exports
 *    one; the importer picks it), the original importer runs again.
 * 2. If both attempts fail, the boundary shows the failure; the next
 *    navigation renders a new lazy instance, so the route loads once the
 *    module is reachable again.
 */
export function lazyWithRetry<M extends Record<K, AnyComponent>, K extends string = "default">(
  load: () => Promise<M>,
  exportName: K = "default" as K,
): ComponentType<PropsOf<M[K]>> {
  // Props are checked against the export's own type at the call site; inside,
  // the loaded component only needs to accept them.
  type Loaded = ComponentType<object>;

  async function retry(error: unknown): Promise<Loaded> {
    const url = exportName === "default" ? null : freshModuleUrl(error);
    if (url) {
      let fresh: Record<string, unknown>;
      try {
        fresh = (await importFresh(url)) as Record<string, unknown>;
      } catch {
        throw error;
      }
      const component = exportFrom(fresh, exportName);
      if (component) return component as Loaded;
    }
    return (await load())[exportName] as unknown as Loaded;
  }

  async function loadComponent(): Promise<{ default: Loaded }> {
    let module: M;
    try {
      module = await load();
    } catch (error) {
      return { default: await retry(error) };
    }
    return { default: module[exportName] as unknown as Loaded };
  }

  function attemptUnder(navigation: string | null) {
    const attempt = {
      navigation,
      failed: false,
      Component: lazy(() => loadComponent().catch((error: unknown) => {
        attempt.failed = true;
        throw error;
      })),
    };
    return attempt;
  }

  let attempt = attemptUnder(null);
  function componentFor(navigation: string | null) {
    if (attempt.failed && navigation !== null && attempt.navigation !== navigation) {
      attempt = attemptUnder(navigation);
    } else if (attempt.navigation === null) {
      attempt.navigation = navigation;
    }
    return attempt.Component;
  }

  function LazyRoute(props: object) {
    return createElement(componentFor(useContext(LazyNavigationContext)), props);
  }
  return LazyRoute as unknown as ComponentType<PropsOf<M[K]>>;
}
